export interface IOrderKmFields {
  manualFullKm: number | null;
  manualEmptyKm: number | null;
  autoEmptyKm: number | null;
}

export interface IOrderKmBreakdown {
  fullKm: number;
  emptyKm: number;
  totalKm: number;
}

/**
 * Resolves the loaded ("volle"), empty ("lege") and total kilometers to display for an order.
 * Each value falls back independently: the planner-registered value wins, otherwise the
 * route-calculated line distance for loaded km and the auto-calculated empty km for empty km.
 * Mirrors what the planning board card shows so that documents never show different kilometers
 * than the planner entered.
 */
export function getOrderKmBreakdown(order: IOrderKmFields, lines: Array<{ distance: number }>): IOrderKmBreakdown {
  const routeMeters = lines.reduce((total, line) => total + Math.max(0, line.distance), 0);
  const fullKm = order.manualFullKm ?? Math.round(routeMeters / 1000);
  const emptyKm = order.manualEmptyKm ?? order.autoEmptyKm ?? 0;

  return { fullKm, emptyKm, totalKm: fullKm + emptyKm };
}

export interface IOrderSustainabilityLineFigures {
  distanceMeters: number;
  emissionsGrams: number;
}

export interface IOrderSustainabilityFigures {
  totalDistanceMeters: number;
  totalEmissionsGrams: number;
  /** Per-line figures, in the same order as the `lines` argument. */
  lines: IOrderSustainabilityLineFigures[];
}

/**
 * Resolves the distance and emissions to report on the sustainability surfaces of an order.
 *
 * The stored `OrderLine.distance` / `OrderLine.emissions` are route (Mapbox) values, which ignore
 * the kilometers the planner actually registered. Since emissions are linear in distance
 * (`km x tonnage x multiplier`), the driven figures are the stored ones scaled by
 * `drivenMeters / routeMeters` — which also preserves the emission multiplier that was in force
 * when the line was calculated.
 *
 * Units match the stored columns: meters for distance, grams for emissions. Totals are the sums of
 * the per-line values so a report's rows always add up to its total.
 */
export function getOrderSustainabilityFigures(
  order: IOrderKmFields,
  lines: Array<{ distance: number; emissions: number }>,
): IOrderSustainabilityFigures {
  const routeMeters = lines.reduce((total, line) => total + Math.max(0, line.distance), 0);
  // Same precedence as `getOrderKmBreakdown`, but the route fallback keeps its full meter
  // precision: rounding it to whole km first would rescale every stored figure of an order
  // without registered km (924,450m → 924,000m), silently changing API output.
  const fullMeters = order.manualFullKm != null ? order.manualFullKm * 1000 : routeMeters;
  const drivenMeters = fullMeters + getOrderKmBreakdown(order, lines).emptyKm * 1000;

  if (lines.length === 0) {
    return { totalDistanceMeters: drivenMeters, totalEmissionsGrams: 0, lines: [] };
  }

  let lineFigures: IOrderSustainabilityLineFigures[];
  if (routeMeters > 0) {
    lineFigures = lines.map((line) => ({
      distanceMeters: Math.round((Math.max(0, line.distance) * drivenMeters) / routeMeters),
      emissionsGrams: Math.round((line.emissions * drivenMeters) / routeMeters),
    }));
  } else {
    // No route distance to scale by (nothing calculated yet): spread the driven meters evenly and
    // leave emissions at zero — such a line has no stored emissions to scale in the first place.
    const base = Math.floor(drivenMeters / lines.length);
    const remainder = drivenMeters - base * lines.length;
    lineFigures = lines.map((_line, index) => ({
      distanceMeters: base + (index < remainder ? 1 : 0),
      emissionsGrams: 0,
    }));
  }

  return {
    totalDistanceMeters: lineFigures.reduce((total, line) => total + line.distanceMeters, 0),
    totalEmissionsGrams: lineFigures.reduce((total, line) => total + line.emissionsGrams, 0),
    lines: lineFigures,
  };
}

/**
 * Replaces each line's stored route `distance` / `emissions` with the driven figures used on
 * customer-facing APIs and portals (TRA-128). Other line fields are left untouched.
 */
export function withDrivenLineDistanceAndEmissions<T extends { distance: number; emissions: number }>(
  order: IOrderKmFields,
  lines: T[],
): T[] {
  const figures = getOrderSustainabilityFigures(order, lines);
  return lines.map((line, index) => ({
    ...line,
    distance: figures.lines[index].distanceMeters,
    emissions: figures.lines[index].emissionsGrams,
  }));
}
